'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/navigation';
import { classNames } from '@/shared/lib/classnames/classnames';
import { formatMoney } from '@/shared/lib/string/formatMoney';
import { UnderlineEffect } from '@/shared/ui/underline-effect';
import btnIcons from '@/shared/assets/sprites/btns.sprite.svg';
import type { AppointmentsData, IMedcenter } from '@/entities/medcenter/model/types';
interface Props {
  medcenter: IMedcenter;
  titleTag?: React.ElementType;
  titleClasses?: string;
  priceClasses?: string;
  children?: React.ReactNode;
  withMedcenterLink?: boolean;
}
export const DoctorPrice: React.FC<Props> = props => {
  const {
    medcenter,
    withMedcenterLink = false,
    titleTag: TitleTag,
    titleClasses,
    priceClasses,
    children
  } = props;
  const t = useTranslations();
  const pathname = usePathname();
  const renderPrice = (appointmentType?: AppointmentsData) => {
    if (!appointmentType) {
      return <span className="text-end text-gray-400">{t('doctor.price.no_price')}</span>;
    }
    const {
      price,
      priceWithDiscount
    } = appointmentType;
    if (!price && price !== 0) return <span className="text-end text-gray-400">{t('doctor.price.no_price')}</span>;
    if (priceWithDiscount && price) {
      return <div className="text-end">
          <span className="mr-1 text-end text-sm font-medium text-statusError">
            <del>{formatMoney(price)} ₸</del>
          </span>
          <span className="text-end text-sm font-medium">
            {t('doctor.price.price_wrapper', {
            price: formatMoney(priceWithDiscount)
          })}
          </span>
        </div>;
    }
    if (price) {
      return <span className="text-end text-sm font-medium">
          {t('doctor.price.price_wrapper', {
          price: formatMoney(price)
        })}
        </span>;
    } else if (price === 0) {
      return <span className="text-end text-sm font-medium">{t('doctor.price.free')}</span>;
    }
  };
  const appointments = medcenter?.appointments || {};
  return <>
      {TitleTag && <TitleTag className={classNames('mb-1 pb-3', {}, [titleClasses])}>{t('doctor.price.title')}</TitleTag>}

      {withMedcenterLink && <Link prefetch={false} className="block" href={`/${pathname.replace('/kk/', '/').split('/')[1]}/medcenter/${medcenter.alias}`}>
          <div className="flex items-center justify-between pb-3">
            <UnderlineEffect size="md">
              <span className="text-sm">{t('doctor.profile.to_medcenter')}</span>
            </UnderlineEffect>
            <span className="flex size-6 items-center justify-center rounded-full bg-bgSecondary">
              <svg className="h-2.5 w-1.5 stroke-textPrimary stroke-1.5">
                <use xlinkHref={`${btnIcons}#arrow`}></use>
              </svg>
            </span>
          </div>
        </Link>}

      <div className={classNames('space-y-0.5 text-sm', {}, [priceClasses])}>
        <div className="grid grid-cols-[1fr_1.5fr]">
          <span className="text-textSecondary">{t('doctor.price.primary')}</span>
          {renderPrice(appointments.primary)}
        </div>
        <div className="grid grid-cols-[1fr_1.5fr]">
          <span className="text-textSecondary">{t('doctor.price.secondary')}</span>
          {renderPrice(appointments.secondary)}
        </div>
        <div className="grid grid-cols-[1fr_1.5fr]">
          <span className="text-textSecondary">{t('doctor.price.online')}</span>
          {renderPrice(appointments.online)}
        </div>
        <div className="flex justify-between">
          <span className="text-textSecondary">{t('doctor.price.description')}</span>
        </div>
      </div>

      {children}
    </>;
};